import React, { useEffect, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomTransComponent } from '../../../components/CustomTransComponent';
import { Stack, Box, Radio, Typography, TextField, InputAdornment, Checkbox } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import SearchIcon from '@mui/icons-material/Search';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { Hierarchy, renderGroupHierarchyIcons } from '../../../components';
import { useGetAllGroupsMutation } from '@fiji/common/src/features/group/groupApi';
import { useLazyGetAllLoadsQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useTheme } from '@mui/material/styles';
import { GroupHierarchySkeleton } from 'pages/GroupHierarchy/GroupHierarchySkeleton';
import type { HierarchyFilter } from 'components/Hierarchy/types';
import { useSearchDeviceOrGroup } from '@fiji/common/src/hooks';

type DeviceLoadHierarchyProps = {
    selectionType?: 'checkbox' | 'radio';
    selectedLoadId?: string | string[];
    onLoadSelect?: (selectedNodeData: any) => void;
};

const DEFAULT_FILTERS: HierarchyFilter[] = [
    {
        key: 'type',
        operator: '!==',
        value: 'LOAD',
        action: 'disableCheck' as HierarchyFilter['action'],
    },
];

const areSelectedLoadIdsEqual = (
    left: string | string[] | undefined,
    right: string | string[] | undefined
): boolean => {
    if (Array.isArray(left) && Array.isArray(right)) {
        return left.length === right.length && left.every((value, index) => value === right[index]);
    }

    return left === right;
};

const arePropsEqual = (prevProps: DeviceLoadHierarchyProps, nextProps: DeviceLoadHierarchyProps): boolean =>
    prevProps.selectionType === nextProps.selectionType &&
    prevProps.onLoadSelect === nextProps.onLoadSelect &&
    areSelectedLoadIdsEqual(prevProps.selectedLoadId, nextProps.selectedLoadId);

export const DeviceLoadHierarchy = React.memo((props: DeviceLoadHierarchyProps): JSX.Element => {
    const { t } = useTranslation();
    const theme: any = useTheme();
    const currentOrgId = useTypedSelector((state) => selectedOrg(state)?.id);
    const currentOrgName = useTypedSelector((state) => selectedOrg(state)?.name);
    const currentOrgGroupCount = useTypedSelector((state) => selectedOrg(state)?.groupCount);
    const currentOrgDeviceCount = useTypedSelector((state) => selectedOrg(state)?.deviceCount);
    const currentOrgDefaultGroupAccess = useTypedSelector((state) => selectedOrg(state)?.defaultGroupAccess);
    const [searchKey, setSearchKey] = React.useState('');
    const [groupsData, setGroupsData] = React.useState<any>();
    const [hierarchyMappingData, setHierarchyMappingData] = React.useState<any>({});
    const [loadingHierarchyNode, setLoadingHierarchyNode] = React.useState<string[]>([]);

    const { currentData: filteredRecords, isLoading: searchLoader } = useSearchDeviceOrGroup({
        searchKey: searchKey || undefined,
    });

    const [getAllGroups, { data: groupsHierarchy, isLoading: isGroupsLoading }] = useGetAllGroupsMutation();
    const [getLoads] = useLazyGetAllLoadsQuery();

    useEffect(() => {
        if (groupsHierarchy?.data?.records) {
            const records = groupsHierarchy.data.records.map((r: any) => ({
                ...r,
                type: r.type || 'GROUP',
            }));
            setGroupsData(records);
        }
    }, [groupsHierarchy]);

    React.useEffect(() => {
        setGroupsData(undefined);
        setHierarchyMappingData({});
        setLoadingHierarchyNode([]);

        if (currentOrgId) {
            void getAllGroups({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrgId]);

    const selectionType = props?.selectionType || 'radio';
    const selectedNodes = selectionType === 'checkbox' ? props?.selectedLoadId || [] : props?.selectedLoadId;

    const hierarchyData = React.useMemo(() => {
        if (searchKey?.length > 0) {
            return filteredRecords;
        }

        if (currentOrgDefaultGroupAccess) {
            return [
                {
                    id: currentOrgId,
                    name: currentOrgName,
                    groupCount: currentOrgGroupCount,
                    deviceCount: currentOrgDeviceCount,
                    defaultGroupAccess: currentOrgDefaultGroupAccess,
                    type: 'ORGANIZATION',
                    icon: { web: { family: 'material-ui', name: 'CorporateFare' } },
                },
            ];
        }

        return groupsData;
    }, [
        currentOrgDefaultGroupAccess,
        currentOrgDeviceCount,
        currentOrgGroupCount,
        currentOrgId,
        currentOrgName,
        filteredRecords,
        groupsData,
        searchKey,
    ]);

    const handleFetchHierarchy = async (parentData: any): Promise<void> => {
        setLoadingHierarchyNode((prevState) => [...prevState, parentData?.id]);

        try {
            if (parentData.type === 'DEVICE') {
                const result = await getLoads({
                    deviceId: parentData.id,
                    params: { page: 0, size: 100 },
                }).unwrap();

                const loads =
                    result?.data?.loadConfiguration?.map((load: any) => ({
                        ...load,
                        id: load.id,
                        deviceId: parentData.id,
                        deviceName: parentData.name,
                        deviceTypeId: parentData.deviceTypeId,
                        deviceIcon: parentData.icon,
                        groupId: parentData.groupId,
                        groupPath: parentData.groupPath,
                        name: load.loadName,
                        type: 'LOAD',
                        loadNumber: load.loadNumber,
                        icon: load.icon || {},
                    })) || [];

                if (loads.length === 0) {
                    loads.push({
                        id: `no-load-${parentData.id}`,
                        name: t('WIDGETS:BALANCING.NO_CONTROLLABLE_LOADS'),
                        type: 'NO_LOADS',
                        sx: { color: 'text.primary', fontSize: '14px' },
                    });
                }

                setHierarchyMappingData((prevState: any) => ({
                    ...prevState,
                    [parentData?.id]: loads,
                }));
            } else {
                const { data: childHierarchyData }: any = await getAllGroups({ parent: parentData?.id });
                if (childHierarchyData) {
                    const records = childHierarchyData?.data?.records?.map((r: any) => ({
                        ...r,
                        type: r.type || (r.deviceCount !== undefined ? 'GROUP' : 'DEVICE'),
                    }));
                    setHierarchyMappingData((prevState: any) => ({
                        ...prevState,
                        [parentData?.id]: records,
                    }));
                }
            }
        } catch (error) {
            console.error('Failed to fetch hierarchy data', error);
        } finally {
            setLoadingHierarchyNode((prevState) => prevState.filter((loadingNode) => loadingNode !== parentData?.id));
        }
    };

    const selectionHandler = (selectedNodeData: any): void => {
        if (selectedNodeData.type === 'LOAD') {
            props.onLoadSelect?.(selectedNodeData);
        }
    };

    const getLoadNumber = (data: any): JSX.Element => (
        <Box
            sx={{
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: '#727e84',
                color: '#fff',
                minWidth: '20px',
                height: '20px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
            }}
        >
            {parseInt(data?.['loadNumber'] ?? 0) + 1}
        </Box>
    );

    const renderCustomIcons = (
        treeItem: any,
        _selectionType: any,
        _selectedNodes: any,
        hierarchyTheme: any,
        labelColor: any
    ): any => {
        if (treeItem?.type === 'LOAD') {
            return getLoadNumber(treeItem);
        }
        if (treeItem?.type === 'NO_LOADS') {
            return null;
        }
        return renderGroupHierarchyIcons(treeItem, _selectionType, _selectedNodes, hierarchyTheme, labelColor);
    };

    const getGroupList = (): JSX.Element =>
        groupsData ? (
            <Stack spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('WIDGETS:BALANCING.SEARCH_GROUPS_AND_DEVICES')}
                    value={searchKey}
                    onChange={(e) => setSearchKey(e.target.value)}
                    sx={{
                        backgroundColor: '#fff',
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                        },
                        m: '16px !important',
                        width: 'auto',
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    <Hierarchy
                        isLoading={!groupsData || searchLoader}
                        data={hierarchyData}
                        hasChild={(treeItem: any): boolean =>
                            treeItem?.groupCount > 0 || treeItem?.deviceCount > 0 || treeItem?.type === 'DEVICE'
                        }
                        labelKey={'name'}
                        hierarchyMappingData={hierarchyMappingData}
                        loadingNode={loadingHierarchyNode}
                        handleTreeNodeSelection={selectionHandler}
                        getAvatarChild={renderCustomIcons}
                        autoChildSelection={false}
                        loadMore={handleFetchHierarchy}
                        loadingSkeleton={<GroupHierarchySkeleton row={1} />}
                        selectedNodes={selectedNodes}
                        selectionType={selectionType}
                        actions={({ treeItem }: any) => {
                            if (treeItem?.type === 'LOAD') {
                                if (selectionType === 'checkbox') {
                                    return (
                                        <Checkbox
                                            checked={
                                                Array.isArray(props?.selectedLoadId) &&
                                                props.selectedLoadId.includes(treeItem.id)
                                            }
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                e.stopPropagation();
                                                selectionHandler(treeItem);
                                            }}
                                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                                        />
                                    );
                                }
                                return (
                                    <Radio
                                        checked={props.selectedLoadId === treeItem.id}
                                        value={treeItem.id}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            e.stopPropagation();
                                            selectionHandler(treeItem);
                                        }}
                                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                                    />
                                );
                            }
                            return <React.Fragment />;
                        }}
                        filters={DEFAULT_FILTERS}
                    />
                </Box>
            </Stack>
        ) : isGroupsLoading ? (
            <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
                <GroupHierarchySkeleton row={1} />
            </Box>
        ) : (
            <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
                <Stack spacing={3} alignItems="center" textAlign="center">
                    <BoltIcon
                        sx={{
                            fontSize: 100,
                            color: theme.palette.action.active,
                        }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        <CustomTransComponent translationKey={'WIDGETS:BALANCING.NO_CONTROLLABLE_LOADS'} />
                    </Typography>
                </Stack>
            </Box>
        );

    return (
        <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {getGroupList()}
        </Box>
    );
}, arePropsEqual);
